/* eslint-disable react-refresh/only-export-components */
import {
  ButtonHTMLAttributes,
  CSSProperties,
  Dispatch,
  ForwardRefRenderFunction,
  HTMLAttributes,
  HTMLProps,
  MouseEventHandler,
  MutableRefObject,
  ReactElement,
  ReactNode,
  Ref,
  SetStateAction,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useClick,
  useHover,
  safePolygon,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  Placement,
  FloatingPortal,
  FloatingFocusManager,
  useId
} from "@floating-ui/react";

interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: "click" | "hover";
  hoverDelay?: number | { open?: number; close?: number };
  matchReferenceWidth?: boolean;
  /** Gap (px) between the trigger and the floating content. Defaults to 5. */
  offsetValue?: number;
  /**
   * Close the popover when a scrollable ancestor (or the window) scrolls.
   * Recommended for menus anchored to items inside a scroll container so they
   * don't float away from / clip past their trigger. Defaults to false.
   */
  dismissOnScroll?: boolean;
  /**
   * Keep the popover on its given placement instead of flipping to the opposite
   * side when it runs out of room. Use for panels whose height changes while
   * open (collapsible sections, async lists) — with `flip` those re-measure on
   * every content change and visibly jump from above the trigger to below it.
   * The content is capped to the space actually available, so pair it with a
   * scrollable body. Defaults to false.
   */
  disableFlip?: boolean;
  /**
   * Upper bound (px) for the floating element, applied alongside the space
   * available on screen. Only takes effect with `disableFlip`.
   */
  maxHeight?: number;
  /**
   * Which element receives focus when the content opens — a tabbable index into
   * the content, or a ref. Pass -1 to leave focus untouched, which is required
   * when the trigger is the input the user keeps typing into (search/typeahead);
   * otherwise the content steals focus on the first keystroke. Defaults to the
   * first tabbable element in the content.
   */
  initialFocus?: number | MutableRefObject<HTMLElement | null>;
}

// Floor for the capped height: below this the panel is too short to be usable,
// and letting it spill past the viewport edge beats showing a two-row scroller.
const MIN_FLOATING_HEIGHT = 200;

export function usePopover({
  initialOpen = false,
  placement = "bottom",
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  trigger = "click",
  hoverDelay,
  matchReferenceWidth = false,
  offsetValue = 5,
  dismissOnScroll = false,
  disableFlip = false,
  maxHeight,
  initialFocus
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      ...(disableFlip
        ? []
        : [
            flip({
              crossAxis: placement.includes("-"),
              fallbackAxisSideDirection: "end",
              padding: 5
            })
          ]),
      shift({ padding: 5 }),
      ...(matchReferenceWidth || disableFlip
        ? [
            size({
              padding: 5,
              apply({ rects, elements, availableHeight }) {
                const styles: Partial<CSSStyleDeclaration> = {};
                if (matchReferenceWidth)
                  styles.width = `${rects.reference.width}px`;
                // Without flip the panel can only grow away from the trigger, so
                // cap it to the room that is actually there and let it scroll.
                if (disableFlip)
                  styles.maxHeight = `${Math.max(
                    Math.min(availableHeight, maxHeight ?? availableHeight),
                    MIN_FLOATING_HEIGHT
                  )}px`;
                Object.assign(elements.floating.style, styles);
              }
            })
          ]
        : [])
    ]
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null && trigger === "click"
  });
  const hover = useHover(context, {
    enabled: trigger === "hover",
    handleClose: safePolygon(),
    delay: hoverDelay
  });
  const dismiss = useDismiss(context, { ancestorScroll: dismissOnScroll });
  const role = useRole(context);

  const interactions = useInteractions([click, hover, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      initialFocus,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      initialFocus,
      labelId,
      descriptionId
    ]
  );
}

type ContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: Dispatch<SetStateAction<string | undefined>>;
      setDescriptionId: Dispatch<SetStateAction<string | undefined>>;
    })
  | null;

const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = useContext(PopoverContext);

  if (context == null)
    throw new Error("Popover components must be wrapped in <Popover />");

  return context;
};

export function Popover({
  children,
  modal = false,
  ...restOptions
}: {
  children: ReactNode;
} & PopoverOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
}

interface PopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

export const PopoverTrigger = forwardRef<
  HTMLElement,
  HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger({ children, asChild = false, ...props }, propRef) {
  const context = usePopoverContext();
  const childrenRef = (children as ReactElement & { ref?: Ref<HTMLElement> })
    .ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children))
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        "data-state": context.open ? "open" : "closed"
      })
    );

  return (
    <div
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? "open" : "closed"}
      {...context.getReferenceProps(props)}
    >
      {children}
    </div>
  );
});

function PopoverContentComponent(
  {
    style,
    initialFocus,
    ...props
  }: HTMLAttributes<HTMLDivElement> & {
    style: CSSProperties;
    /** Forwarded to FloatingFocusManager. Pass -1 to skip auto-focusing on open. */
    initialFocus?: number | MutableRefObject<HTMLElement | null>;
  },
  propRef
) {
  const { context: floatingContext, ...context } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        initialFocus={initialFocus ?? context.initialFocus}
      >
        <div
          ref={ref}
          style={{
            // @ts-ignore -- floating styles doesnt exist
            ...context.floatingStyles,
            ...(style as CSSProperties)
          }}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps(props)}
        >
          {props.children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

export const PopoverContent = forwardRef<
  HTMLDivElement,
  HTMLProps<HTMLDivElement> & {
    initialFocus?: number | MutableRefObject<HTMLElement | null>;
  }
>(
  PopoverContentComponent as ForwardRefRenderFunction<
    HTMLDivElement,
    HTMLProps<HTMLDivElement> & {
      initialFocus?: number | MutableRefObject<HTMLElement | null>;
    }
  >
);

export const PopoverHeading = forwardRef<
  HTMLHeadingElement,
  HTMLProps<HTMLHeadingElement>
>(function PopoverHeading(props, ref) {
  const { setLabelId } = usePopoverContext();
  const id = useId();

  // Only sets `aria-labelledby` on the Popover root element
  // if this component is mounted inside it.
  useLayoutEffect(() => {
    setLabelId(id);
    return () => setLabelId(undefined);
  }, [id, setLabelId]);

  return (
    <h2 {...props} ref={ref} id={id}>
      {props.children}
    </h2>
  );
});

export const PopoverDescription = forwardRef<
  HTMLParagraphElement,
  HTMLProps<HTMLParagraphElement>
>(function PopoverDescription(props, ref) {
  const { setDescriptionId } = usePopoverContext();
  const id = useId();

  // Only sets `aria-describedby` on the Popover root element
  // if this component is mounted inside it.
  useLayoutEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(undefined);
  }, [id, setDescriptionId]);

  return <p {...props} ref={ref} id={id} />;
});

function PopoverCloseComponents(
  props: ButtonHTMLAttributes<HTMLButtonElement> & {
    onClick: (e: MouseEventHandler<HTMLButtonElement>) => unknown;
  },
  ref
) {
  const { setOpen } = usePopoverContext();

  return (
    <button
      type="button"
      ref={ref}
      {...props}
      onClick={(event) => {
        props.onClick(event);
        setOpen(false);
      }}
    />
  );
}

export const PopoverClose = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(
  PopoverCloseComponents as ForwardRefRenderFunction<
    HTMLButtonElement,
    ButtonHTMLAttributes<HTMLButtonElement>
  >
);
